// Vendor
import { FunctionComponent, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

// Components
import Button, {
  ButtonColor,
  ButtonSize,
  ButtonType,
  ButtonVariant
} from 'src/components/atoms/Button';
import Gap from 'src/components/atoms/Gap';
import { Loader } from 'src/components/atoms/Loader';
import Text, { TextColor, TextVariant } from 'src/components/atoms/Text';
import {
  FormValues,
  InvitationDetailsForm
} from 'src/components/molecules/Invitation/InvitationDetailsForm';
import { InvitationErrorMessage } from 'src/components/molecules/Invitation/InvitationErrorMessage';
import { InvitationOtpForm } from 'src/components/molecules/Invitation/InvitationOtpForm';
import { Invitation } from 'src/components/templates';

// Redux
import { actions as authActions } from 'src/features/auth/authSlice';
import {
  actions as invitationActions,
  invitationCreateUserWithTokenThunk,
  invitationSendOtpThunk,
  invitationVerifyOtpThunk,
  selectors,
  useInvitation
} from 'src/features/invitation';
import { useAppDispatch } from 'src/store';
import { Status } from 'src/ts/enums';
import { IInvitation } from 'src/ts/interfaces';

// Constants
import { ROLE_DESCRIPTION } from 'src/features/auth/constants';

const RESEND_COOLDOWN_SECONDS = 30;

// Distinct, on-brand copy per backend status. The page (single call site) owns this
// mapping and picks inline vs banner placement — InvitationOtpForm is presentational.
const SEND_ERROR_COPY: Record<number, string> = {
  422: 'This invitation has expired. Please ask your building manager to re-invite you.',
  429: "You've requested too many codes. Please wait a moment and try again."
};
const VERIFY_INLINE_422 =
  'That code is incorrect or has expired. Check it and try again, or resend a new one.';
const VERIFY_BANNER_429 = 'Too many attempts. For your security, please request a new code.';
const GENERIC_ERROR = 'Something went wrong. Please try again.';
// The verified token is short-lived and in-memory only; if it expires before the user
// finishes setting a password, create_user returns 401 and we send them back to request
// a fresh code rather than failing hard.
const SESSION_EXPIRED_NOTICE =
  'Your verification expired. Request a new code to finish creating your account.';

const buildDefaultValues = (invitation?: IInvitation | null): FormValues => ({
  firstName: invitation?.first_name ?? '',
  lastName: invitation?.last_name ?? '',
  role: invitation?.role ? ROLE_DESCRIPTION[invitation.role] : '',
  email: invitation?.email ?? '',
  password: '',
  confirmPassword: ''
});

type Step = 'collect-email-cta' | 'enter-pin' | 'details';

const InvitationPage: FunctionComponent = () => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const { data, isError, isLoading } = useInvitation(id);

  // The verified invitation (PII) and the account-creation token live in component
  // state only — never in Redux, never persisted (no DevTools/localStorage exposure).
  const [step, setStep] = useState<Step>('collect-email-cta');
  const [verifiedInvitation, setVerifiedInvitation] = useState<IInvitation | null>(null);
  const [verifiedToken, setVerifiedToken] = useState<string | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  const sendStatus = useSelector(selectors.sendOtp.status);
  const sendError = useSelector(selectors.sendOtp.error);
  const verifyStatus = useSelector(selectors.verifyOtp.status);
  const verifyError = useSelector(selectors.verifyOtp.error);

  // An invitation carries expires_at once created; a null/absent expires_at means a
  // legacy pre-PIN-flow invitation, which the backend now treats as terminal
  const isExpired = useMemo(
    () => !!data && (data.expires_at == null || new Date(data.expires_at).getTime() < Date.now()),
    [data]
  );

  const goToLogin = () => dispatch(authActions.login());

  const handleSendCode = async () => {
    if (!id) return;
    setSessionExpired(false);
    const result = await dispatch(invitationSendOtpThunk(id));
    if (invitationSendOtpThunk.fulfilled.match(result)) {
      setStep('enter-pin');
    }
  };

  const handleVerify = async (otp: string) => {
    if (!id) return;
    const result = await dispatch(invitationVerifyOtpThunk({ id, otp }));
    if (invitationVerifyOtpThunk.fulfilled.match(result)) {
      setVerifiedInvitation(result.payload.invitation);
      setVerifiedToken(result.payload.token);
      setStep('details');
    }
  };

  const handleResend = () => {
    if (!id) return;
    dispatch(invitationActions.resetSendOtp());
    dispatch(invitationActions.resetVerifyOtp());
    dispatch(invitationSendOtpThunk(id));
  };

  const handleNewFlowSubmit = async ({ password }: Pick<FormValues, 'password'>) => {
    if (!id || !verifiedToken) return;
    const result = await dispatch(
      invitationCreateUserWithTokenThunk({ id, password, token: verifiedToken })
    );
    // The token is short-lived and in-memory only; a 401 means it expired before the
    // user finished. Recover by sending them back to request a fresh code.
    if (invitationCreateUserWithTokenThunk.rejected.match(result) && result.payload === 401) {
      setVerifiedToken(null);
      setVerifiedInvitation(null);
      dispatch(invitationActions.resetVerifyOtp());
      setSessionExpired(true);
      setStep('collect-email-cta');
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  const renderContent = () => {
    // A 403 on read/send/verify means the invitation can't be used — same terminal
    // state as a failed GET.
    if (isError || isExpired || sendError === 403 || verifyError === 403) {
      return <InvitationErrorMessage onClick={goToLogin} />;
    }

    if (step === 'details') {
      return (
        <InvitationDetailsForm
          defaultValues={buildDefaultValues(verifiedInvitation)}
          onSubmit={handleNewFlowSubmit}
        />
      );
    }

    if (step === 'enter-pin') {
      const inlineError = verifyError === 422 ? VERIFY_INLINE_422 : null;
      let bannerError: string | null = null;
      if (verifyError === 429) {
        bannerError = VERIFY_BANNER_429;
      } else if (sendError != null) {
        bannerError = SEND_ERROR_COPY[sendError] ?? GENERIC_ERROR;
      } else if (verifyError != null && verifyError !== 422) {
        bannerError = GENERIC_ERROR;
      }

      return (
        <InvitationOtpForm
          obfuscatedEmail={data?.obfuscated_email ?? ''}
          onVerify={handleVerify}
          onResend={handleResend}
          isVerifying={verifyStatus === Status.LOADING}
          inlineError={inlineError}
          bannerError={bannerError}
          resendCooldownSeconds={RESEND_COOLDOWN_SECONDS}
        />
      );
    }

    // step === 'collect-email-cta'
    const ctaBanner = sessionExpired
      ? SESSION_EXPIRED_NOTICE
      : sendError != null
      ? SEND_ERROR_COPY[sendError] ?? GENERIC_ERROR
      : null;

    return (
      <>
        <Text color={TextColor.initial} variant={TextVariant.h3}>
          Verify your email
        </Text>
        <Gap height={1} />
        <Text color={TextColor.initial} variant={TextVariant.normal} isStretched>
          We&apos;ll send a one-time code to {data?.obfuscated_email ?? 'your email'} to confirm
          it&apos;s you before you set up your account.
        </Text>
        <Gap height={1.5} />
        {ctaBanner && (
          <>
            <Text color={TextColor.error} variant={TextVariant.small} isStretched>
              {ctaBanner}
            </Text>
            <Gap height={1} />
          </>
        )}
        <Button
          name="SendInvitationOtp"
          variant={ButtonVariant.contained}
          color={ButtonColor.primary}
          size={ButtonSize.normal}
          type={ButtonType.button}
          isDisabled={sendStatus === Status.LOADING}
          onClick={handleSendCode}
        >
          Send me a code
        </Button>
      </>
    );
  };

  return <Invitation>{renderContent()}</Invitation>;
};

export default InvitationPage;
